import type { ColumnDef } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import { DataError } from '~/modules/artist-roster/components/DataError/DataError';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import type {
  ArtistTableHeaderMetaType,
  ArtistTableMetaType,
  ArtistType,
} from '../../lib/types';
import { SkeletonTable } from '../Table/SkeletonTable';

interface ArtistsTableProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<ArtistType, any>[];
  data: ArtistType[];
  isLoading: boolean;
  meta?: ArtistTableMetaType;
  className?: string;
}

export function ArtistsTable({
  columns,
  data,
  meta,
  isLoading,
  className,
}: ArtistsTableProps) {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const table = useReactTable({
    data,
    columns,
    meta,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleArtistLinkClick = ({
    artistData,
  }: {
    artistData: ArtistType;
  }) => {
    const tableMeta = table.options?.meta as ArtistTableMetaType;
    const {
      filtersCount = 0,
      crossoverFilterApplied = false,
      filtersApplied = false,
      rosterStatus = 'all',
    } = tableMeta || {};

    trackEvent('rosterTableArtistClick', {
      artist_id: artistData.partyId,
      artist_name: artistData.name,
      roster_label_filters_count: filtersCount,
      roster_crossover_filter_applied: crossoverFilterApplied,
      roster_label_filters_applied: filtersApplied,
      roster_status: rosterStatus,
    });
  };

  return (
    <Table
      aria-label="Artists roster list"
      className={cn('w-full table-fixed text-left', className)}
    >
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const headerMeta = header.column.columnDef
                .meta as ArtistTableHeaderMetaType;

              return (
                <TableHead
                  key={header.id}
                  className={cn(
                    'text-body-sm font-normal first:pl-lg',
                    headerMeta?.className,
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <SkeletonTable
            size={12}
            headers={table.getHeaderGroups()[0].headers}
            cellClassName="h-[5rem]"
          />
        ) : table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row, rowIndex) => (
            <TableRow
              aria-label={`Row for ${row.original?.name || 'artist'}`}
              key={row.id}
              data-state={row.getIsSelected() && 'selected'}
              className="group relative duration-300 hover:bg-emphasis-subtle"
            >
              {row.getVisibleCells().map((cell) => {
                return (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      'border-y p-0 border-transparent',
                      'group-hover:border-emphasis-strong',
                      'first:rounded-l-sm first:border-l',
                      'last:rounded-r-sm last:border-r',
                      rowIndex % 2 === 0 && 'bg-default-inverse/default',
                    )}
                  >
                    {cell.column.id === 'name' ? (
                      row.original.partyId ? (
                        <Link
                          to={`/roster/${row.original.partyId}`}
                          className="group/link flex items-center gap-sm py-md pl-xl"
                          onClick={() =>
                            handleArtistLinkClick({ artistData: row.original })
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Link>
                      ) : (
                        <div className="flex items-center gap-sm py-md pl-lg">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      )
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={4}>
              <DataError
                title={t('noData.title')}
                subtitle={t('noData.sub')}
                icon={
                  <Icon
                    type="user-music"
                    size="4x"
                    variant="light"
                    aria-hidden
                    className="text-default"
                  />
                }
              />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

ArtistsTable.displayName = 'ArtistsTable';
